import { InfoIcon } from '@chakra-ui/icons';
import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Button,
  Checkbox,
  Divider,
  FormControl,
  FormLabel,
  HStack,
  Icon,
  Input,
  InputGroup,
  InputRightAddon,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Stack,
  Text,
  Tooltip,
} from '@chakra-ui/react';
import { yupResolver } from '@hookform/resolvers/yup';
import { Select } from 'chakra-react-select';
import dayjs from 'dayjs';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import { useTranslations } from 'use-intl';

import { useCreateOptionAssetMutation } from '@blockpulse3/graphql/hooks';
import {
  ErrorMessage,
  ResponsiveModal,
  ResponsiveModalFooter,
  ResponsiveModalProps,
  useErrorToast,
} from '@blockpulse3/ui/commons';

import { defaultValues, durationUnitOptions, optionAssetTypeOptions, schema } from './schema';
import { IOptionAsset } from './types';

type Props = {
  onClose: () => void;
  onComplete: () => void;
} & Omit<ResponsiveModalProps, 'children' | 'onClose'>;

/**
 * AssetCreateModal.
 * Form to create a Stock Asset.
 *
 * @param {Props}
 * @returns {JSX.Element}
 */
export function OptionCreateModal({ onClose, onComplete, ...props }: Props): JSX.Element {
  const i18nGeneral = useTranslations('General');
  const i18nOptionCreateModal = useTranslations('OptionCreateModal');
  const i18nAssetTypeValues = useTranslations('AssetTypeValues');

  const errorToast = useErrorToast();

  const { companyId = '' } = useParams();
  const [createOptionAsset, { loading: isCreateLoading }] = useCreateOptionAssetMutation();

  const { register, control, formState, handleSubmit, reset, watch } = useForm<IOptionAsset>({
    defaultValues,
    resolver: yupResolver(schema),
  });

  const handleModalClose = (): void => {
    onClose();
    reset({ ...defaultValues });
  };

  const handleFormSubmit: SubmitHandler<IOptionAsset> = (data): void => {
    if (!companyId) return;

    const { hasCurve, cliffDuration, vestingDuration, intervalDuration, ...optionData } = data;

    const optionAsset = {
      ...optionData,
      date: dayjs(data.date).unix(),
      expirationDate: dayjs(data.expirationDate).unix(),
      durationUnit: data.durationUnit.value,
      type: data.type.value,
      vestingCurve: {
        noCurve: !hasCurve,
        cliffDuration,
        vestingDuration,
        intervalDuration,
      },
    };

    createOptionAsset({
      variables: {
        companyId,
        createOptionAssetInput: {
          ...optionAsset,
        },
      },
      onCompleted: () => {
        onComplete();
        handleModalClose();
      },
      onError: () => {
        errorToast({ title: i18nOptionCreateModal('Toast.Error') });
        handleModalClose();
      },
    });
  };

  const optionType = watch('type');
  const durationUnit = watch('durationUnit');
  const hasCurve = watch('hasCurve');

  return (
    <ResponsiveModal onClose={handleModalClose} {...props}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>{i18nOptionCreateModal('Title')}</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <form id="add-option-asset" onSubmit={handleSubmit(handleFormSubmit)}>
            <Stack spacing="4">
              <Controller
                control={control}
                name="type"
                render={({ field }): JSX.Element => (
                  <FormControl isInvalid={!!formState.errors?.type}>
                    <FormLabel htmlFor="type">{i18nOptionCreateModal('OptionType')}</FormLabel>
                    <Select
                      id="type"
                      isSearchable={false}
                      menuPlacement="auto"
                      options={optionAssetTypeOptions}
                      getOptionLabel={(option): string =>
                        option.label ? i18nAssetTypeValues(option.label, { nb: 1 }) : ''
                      }
                      {...field}
                    />
                    <ErrorMessage error={formState.errors?.type?.label} />
                  </FormControl>
                )}
              />
              <Stack direction={{ base: 'column', md: 'row' }} spacing="4">
                <FormControl isInvalid={!!formState.errors?.name} flex="2">
                  <FormLabel htmlFor="name">{i18nOptionCreateModal('Name')}</FormLabel>
                  <Input id="name" type="string" {...register('name')} />
                  <ErrorMessage error={formState.errors?.name} />
                </FormControl>
                <FormControl isInvalid={!!formState.errors?.code} flex="1">
                  <FormLabel htmlFor="code">
                    <HStack>
                      <Text>{i18nOptionCreateModal('Code')}</Text>
                      <Tooltip
                        hasArrow
                        placement="top"
                        label={i18nOptionCreateModal('Tooltip.Code')}
                      >
                        <Icon as={InfoIcon} color="gray.600" />
                      </Tooltip>
                    </HStack>
                  </FormLabel>
                  <Input id="code" type="string" {...register('code')} />
                  <ErrorMessage error={formState.errors?.code} />
                </FormControl>
              </Stack>
              <Stack direction={{ base: 'column', md: 'row' }} spacing="4">
                <FormControl isInvalid={!!formState.errors?.initialSupply}>
                  <FormLabel htmlFor="initialSupply">
                    {i18nOptionCreateModal('InitialSupply')}
                  </FormLabel>
                  <InputGroup>
                    <Input id="initialSupply" type="number" {...register('initialSupply')} />
                    <InputRightAddon>{i18nAssetTypeValues(optionType?.label)}</InputRightAddon>
                  </InputGroup>
                  <ErrorMessage error={formState.errors?.initialSupply} />
                </FormControl>
                <FormControl isInvalid={!!formState.errors?.remainingSupply}>
                  <FormLabel htmlFor="remainingSupply">
                    {i18nOptionCreateModal('RemainingSupply')}
                  </FormLabel>
                  <InputGroup>
                    <Input id="remainingSupply" type="number" {...register('remainingSupply')} />
                    <InputRightAddon>{i18nAssetTypeValues(optionType?.label)}</InputRightAddon>
                  </InputGroup>
                  <ErrorMessage error={formState.errors?.remainingSupply} />
                </FormControl>
              </Stack>
              <Stack direction={{ base: 'column', md: 'row' }} spacing="4">
                <FormControl isInvalid={!!formState.errors?.date}>
                  <FormLabel htmlFor="date">{i18nOptionCreateModal('CreationDate')}</FormLabel>
                  <Input id="date" type="date" {...register('date')} />
                  <ErrorMessage error={formState.errors?.date} />
                </FormControl>
                <FormControl isInvalid={!!formState.errors?.expirationDate}>
                  <FormLabel htmlFor="expirationDate">
                    {i18nOptionCreateModal('ExpirationDate')}
                  </FormLabel>
                  <Input id="expirationDate" type="date" {...register('expirationDate')} />
                  <ErrorMessage error={formState.errors?.expirationDate} />
                </FormControl>
              </Stack>
              <Stack
                direction={{ base: 'column', md: 'row' }}
                w={{ base: '100%', md: '48%' }}
                spacing="4"
              >
                <Controller
                  control={control}
                  name="durationUnit"
                  render={({ field }): JSX.Element => (
                    <FormControl isInvalid={!!formState.errors?.durationUnit}>
                      <FormLabel htmlFor="durationUnit">
                        {i18nOptionCreateModal('DurationUnit')}
                      </FormLabel>
                      <Select
                        id="durationUnit"
                        isSearchable={false}
                        menuPlacement="auto"
                        options={durationUnitOptions}
                        getOptionLabel={(option): string =>
                          option.label ? i18nOptionCreateModal(`DurationUnits.${option.label}`) : ''
                        }
                        {...field}
                      />
                      <ErrorMessage error={formState.errors?.durationUnit?.label} />
                    </FormControl>
                  )}
                />
              </Stack>
              <Stack direction={{ base: 'column', md: 'row' }} spacing="4">
                <FormControl isInvalid={!!formState.errors?.attributionPeriod}>
                  <FormLabel htmlFor="attributionPeriod">
                    {i18nOptionCreateModal('AttributionPeriod')}
                  </FormLabel>
                  <InputGroup>
                    <Input
                      id="attributionPeriod"
                      type="number"
                      {...register('attributionPeriod')}
                    />
                    <InputRightAddon>
                      {i18nOptionCreateModal(`DurationUnits.${durationUnit.label}`)}
                    </InputRightAddon>
                  </InputGroup>
                  <ErrorMessage error={formState.errors?.attributionPeriod} />
                </FormControl>
                <FormControl isInvalid={!!formState.errors?.subscriptionPeriod}>
                  <FormLabel htmlFor="subscriptionPeriod">
                    {i18nOptionCreateModal('SubscriptionPeriod')}
                  </FormLabel>
                  <InputGroup>
                    <Input
                      id="subscriptionPeriod"
                      type="number"
                      {...register('subscriptionPeriod')}
                    />
                    <InputRightAddon>
                      {i18nOptionCreateModal(`DurationUnits.${durationUnit.label}`)}
                    </InputRightAddon>
                  </InputGroup>
                  <ErrorMessage error={formState.errors?.subscriptionPeriod} />
                </FormControl>
              </Stack>
              <Stack direction={{ base: 'column', md: 'row' }} spacing="4">
                <FormControl isInvalid={!!formState.errors?.subscriptionPrice}>
                  <FormLabel htmlFor="subscriptionPrice">
                    {i18nOptionCreateModal('SubscriptionPrice')}
                  </FormLabel>
                  <InputGroup>
                    <Input
                      id="subscriptionPrice"
                      step="0.01"
                      type="number"
                      {...register('subscriptionPrice')}
                    />
                    <InputRightAddon>€</InputRightAddon>
                  </InputGroup>
                  <ErrorMessage error={formState.errors?.subscriptionPrice} />
                </FormControl>
                <FormControl isInvalid={!!formState.errors?.exercisePrice}>
                  <FormLabel htmlFor="exercisePrice">
                    {i18nOptionCreateModal('ExercisePrice')}
                  </FormLabel>
                  <InputGroup>
                    <Input
                      id="exercisePrice"
                      step="0.01"
                      type="number"
                      {...register('exercisePrice')}
                    />
                    <InputRightAddon>€</InputRightAddon>
                  </InputGroup>
                  <ErrorMessage error={formState.errors?.exercisePrice} />
                </FormControl>
              </Stack>
              <Stack
                direction={{ base: 'column', md: 'row' }}
                w={{ base: '100%', md: '48%' }}
                spacing="4"
              >
                <FormControl isInvalid={!!formState.errors?.conversionParity}>
                  <FormLabel htmlFor="conversionParity">
                    {i18nOptionCreateModal('ConversionParity')}
                  </FormLabel>
                  <InputGroup>
                    <Input
                      id="conversionParity"
                      step="0.01"
                      type="number"
                      {...register('conversionParity')}
                    />
                    <InputRightAddon>
                      {i18nAssetTypeValues('ORDINARY_SHARE', { nb: '' })}
                    </InputRightAddon>
                  </InputGroup>
                  <ErrorMessage error={formState.errors?.conversionParity} />
                </FormControl>
              </Stack>
              <FormControl>
                <Checkbox {...register('delegated')}>
                  <Text>{i18nOptionCreateModal('Delegated')}</Text>
                </Checkbox>
              </FormControl>
              <FormControl>
                <Checkbox {...register('transferable')}>
                  <Text>{i18nOptionCreateModal('Transferable')}</Text>
                </Checkbox>
              </FormControl>
              <FormControl>
                <Checkbox {...register('accelerable')}>
                  <Text>{i18nOptionCreateModal('Accelerable')}</Text>
                </Checkbox>
              </FormControl>
              <FormControl>
                <Checkbox {...register('approvable')}>
                  <Text>{i18nOptionCreateModal('Approvable')}</Text>
                </Checkbox>
              </FormControl>
              <Accordion
                allowToggle
                defaultIndex={hasCurve ? [0] : []}
                index={hasCurve ? [0] : []}
                variant="unstyled"
              >
                <AccordionItem>
                  <HStack>
                    <FormControl>
                      <Checkbox {...register('hasCurve')}>
                        <Text fontWeight="semibold">{i18nOptionCreateModal('VestingCurve')}</Text>
                      </Checkbox>
                    </FormControl>
                    <AccordionButton maxW="20px">
                      <AccordionIcon />
                    </AccordionButton>
                  </HStack>
                  <AccordionPanel mt="24px" p="0">
                    <Stack spacing="4">
                      <Stack
                        direction={{ base: 'column', md: 'row' }}
                        w={{ base: '100%', md: '48%' }}
                        spacing="4"
                      >
                        <FormControl isInvalid={!!formState.errors?.cliffDuration}>
                          <FormLabel htmlFor="cliffDuration">
                            {i18nOptionCreateModal('CliffDuration')}
                          </FormLabel>
                          <InputGroup>
                            <Input
                              id="cliffDuration"
                              type="number"
                              {...register('cliffDuration')}
                            />
                            <InputRightAddon>
                              {i18nOptionCreateModal(`DurationUnits.${durationUnit.label}`)}
                            </InputRightAddon>
                          </InputGroup>
                          <ErrorMessage error={formState.errors?.cliffDuration} />
                        </FormControl>
                      </Stack>
                      <Stack direction={{ base: 'column', md: 'row' }} spacing="4">
                        <FormControl isInvalid={!!formState.errors?.vestingDuration}>
                          <FormLabel htmlFor="vestingDuration">
                            {i18nOptionCreateModal('VestingDuration')}
                          </FormLabel>
                          <InputGroup>
                            <Input
                              id="vestingDuration"
                              type="number"
                              {...register('vestingDuration')}
                            />
                            <InputRightAddon>
                              {i18nOptionCreateModal(`DurationUnits.${durationUnit.label}`)}
                            </InputRightAddon>
                          </InputGroup>
                          <ErrorMessage error={formState.errors?.vestingDuration} />
                        </FormControl>
                        <FormControl isInvalid={!!formState.errors?.intervalDuration}>
                          <FormLabel htmlFor="intervalDuration">
                            {i18nOptionCreateModal('IntervalDuration')}
                          </FormLabel>
                          <InputGroup>
                            <Input
                              id="intervalDuration"
                              type="number"
                              {...register('intervalDuration')}
                            />
                            <InputRightAddon>
                              {i18nOptionCreateModal(`DurationUnits.${durationUnit.label}`)}
                            </InputRightAddon>
                          </InputGroup>
                          <ErrorMessage error={formState.errors?.intervalDuration} />
                        </FormControl>
                      </Stack>
                    </Stack>
                  </AccordionPanel>
                </AccordionItem>
              </Accordion>
            </Stack>
          </form>
        </ModalBody>
        <Divider />
        <ResponsiveModalFooter>
          <Button isDisabled={isCreateLoading} type="button" variant="secondary" onClick={onClose}>
            {i18nGeneral('Cancel')}
          </Button>
          <Button
            data-cy="add-option"
            form="add-option-asset"
            isDisabled={isCreateLoading}
            isLoading={isCreateLoading}
            type="submit"
          >
            {i18nOptionCreateModal('AddOption')}
          </Button>
        </ResponsiveModalFooter>
      </ModalContent>
    </ResponsiveModal>
  );
}

export type OptionCreateModalProps = Props;
